import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type Schemas, keys, run, scopeQuery } from "./api"
import type { Scope } from "./connection"
import { type DefinitionSet, toDefinitionSet } from "./defs"
import { parseExact, stringifyExact } from "./lossless"
import { useCell } from "./session"
import type { WhyResponse } from "./why"

/** The whole admitted set, one GET /v1/defs; kind and artifact views select from it. */
export function useDefinitions(scope: Scope) {
  const { connection, client } = useCell()
  return useQuery<DefinitionSet>({
    queryKey: keys.defs(connection.baseUrl, scope),
    // The live definitions subscription invalidates this on every install; a large admitted set
    // (megabytes with Core and imported apps) is not re-read on every navigation in between.
    staleTime: 5 * 60_000,
    // Read as text and parse losslessly so canonical JSON keeps exact numbers.
    queryFn: async ({ signal }) => {
      const text = await run(connection.baseUrl, () => client.GET("/v1/defs", { params: { query: scopeQuery(scope) }, parseAs: "text", signal }))
      return toDefinitionSet(parseExact(text) as Schemas["DefsResponse"])
    },
  })
}

/** The principal the cell resolved for this connection: actor, roles and business units. */
export function usePrincipalRecord(scope: Scope) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: ["principal-record", connection.baseUrl, scope.orgId, scope.environmentId],
    staleTime: 60_000,
    retry: false,
    queryFn: () => run(connection.baseUrl, () => client.GET("/v1/principal", { params: { query: scopeQuery(scope) } })),
  })
}

export const PAGE_SIZE = 50

/** One page of objects of a type (values exact, #2770); `cursor` is the opaque token of the previous page. */
export function useObjects(scope: Scope, objectType: string, cursor: string | null, limit = PAGE_SIZE, enabled = true) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: [...keys.objects(connection.baseUrl, scope, objectType, cursor), limit],
    queryFn: async ({ signal }) =>
      parseExact(
        await run(connection.baseUrl, () =>
          client.GET("/v1/objects/{object_type}", {
            params: { path: { object_type: objectType }, query: { ...scopeQuery(scope), limit, ...(cursor ? { cursor } : {}) } },
            parseAs: "text",
            signal,
          }),
        ),
      ) as Schemas["ObjectList"],
    enabled,
    placeholderData: keepPreviousData,
  })
}

export function useObject(scope: Scope, objectType: string, key: string) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: keys.object(connection.baseUrl, scope, objectType, key),
    queryFn: async ({ signal }) =>
      parseExact(
        await run(connection.baseUrl, () =>
          client.GET("/v1/objects/{object_type}/{key}", { params: { path: { object_type: objectType, key }, query: { ...scopeQuery(scope), includeDeleted: true } }, parseAs: "text", signal }),
        ),
      ) as Schemas["ObjectResponse"],
  })
}

/** The recorded causal chain of an object's current state. */
export function useWhy(scope: Scope, objectType: string, key: string) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: keys.why(connection.baseUrl, scope, objectType, key),
    queryFn: async ({ signal }) =>
      parseExact(await run(connection.baseUrl, () => client.GET("/v1/why/{object_type}/{key}", { params: { path: { object_type: objectType, key }, query: scopeQuery(scope) }, parseAs: "text", signal }))) as WhyResponse,
  })
}

export const LEDGER_PAGE = 100

/** Ledger entries after `after`, optionally restricted to one retained receipt. */
export function useLedger(scope: Scope, after: number, receiptCommit?: number) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: [...keys.ledger(connection.baseUrl, scope, after, LEDGER_PAGE), receiptCommit],
    queryFn: async ({ signal }) =>
      parseExact(await run(connection.baseUrl, () => client.GET("/v1/ledger", { params: { query: { ...scopeQuery(scope), after, limit: LEDGER_PAGE, ...(receiptCommit === undefined ? {} : { receiptCommit }) } }, parseAs: "text", signal }))) as Schemas["LedgerPage"],
    placeholderData: receiptCommit === undefined ? keepPreviousData : undefined,
    refetchInterval: receiptCommit === undefined ? false : 10_000,
  })
}

/** Receipt identity remains stable even when event positions are reused after a restore. */
export function matchesLedgerReceipt(receipt: Schemas["LedgerPage"]["receipt"], commit: number, hash: string | undefined): boolean {
  return Number.isSafeInteger(commit) && commit > 0 && !!hash && receipt?.commitSeq === commit && receipt.hash === hash
}

export const COMMAND_TASK_TYPE = "norn.CommandTask"

export type TaskOperation = "acknowledge" | "release"

/** Acknowledge (close, never resend) or release (assert not applied; allow one retry) a failed command. */
export function useTaskOperation(scope: Scope) {
  const { connection, client } = useCell()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, operation }: { id: string; operation: TaskOperation }) =>
      run(connection.baseUrl, () =>
        operation === "acknowledge"
          ? client.POST("/v1/command-tasks/{id}/acknowledge", { params: { path: { id }, query: scopeQuery(scope) } })
          : client.POST("/v1/command-tasks/{id}/release", { params: { path: { id }, query: scopeQuery(scope) } }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["objects", connection.baseUrl, scope.orgId, scope.environmentId, COMMAND_TASK_TYPE] }),
  })
}

export const FINDINGS_PAGE = 50

/** Server-side finding filters; every one is part of the cursor binding. */
export interface FindingFilters {
  readonly status?: Schemas["DataQualityFindingStatus"]
  readonly severity?: Schemas["DataQualitySeverity"]
  readonly ruleId?: string
  readonly ruleVersion?: string
  readonly subjectType?: string
  readonly subjectKey?: string
}

/** One page of data-quality findings this identity may see (hidden subjects are concealed by the cell). */
export function useFindings(scope: Scope, filters: FindingFilters, cursor: string | null) {
  const { connection, client } = useCell()
  const query = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== "")) as FindingFilters
  return useQuery({
    queryKey: ["findings", connection.baseUrl, scope.orgId, scope.environmentId, query, cursor],
    queryFn: async ({ signal }) =>
      parseExact(
        await run(connection.baseUrl, () =>
          client.GET("/v1/data-quality/findings", {
            params: { query: { ...scopeQuery(scope), ...query, limit: FINDINGS_PAGE, ...(cursor ? { cursor } : {}) } },
            parseAs: "text",
            signal,
          }),
        ),
      ) as Schemas["DataQualityFindingsPage"],
    placeholderData: keepPreviousData,
  })
}

export const LINKS_PAGE = 25

/** One authorized page of an object's one-hop links (a cursor may follow a short or empty page). */
export function useLinkPager(scope: Scope) {
  const { connection, client } = useCell()
  return (o: { objectType: string; key: string }, cursor: string | null, direction: "out" | "in" | "both" = "both", linkType?: string) =>
    run(connection.baseUrl, () =>
      client.GET("/v1/objects/{object_type}/{key}/links", {
        params: { path: { object_type: o.objectType, key: o.key }, query: { ...scopeQuery(scope), direction, limit: LINKS_PAGE, ...(linkType ? { linkType } : {}), ...(cursor ? { cursor } : {}) } },
        parseAs: "text",
      }),
    ).then((text) => parseExact(text) as Schemas["LinkPage"])
}

/** Recorded evidence for every property of one object (never inferred from mappings). */
export function useProvenance(scope: Scope, objectType: string, key: string, enabled: boolean) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: ["provenance", connection.baseUrl, scope.orgId, scope.environmentId, objectType, key],
    enabled,
    queryFn: async ({ signal }) =>
      parseExact(await run(connection.baseUrl, () => client.GET("/v1/objects/{object_type}/{key}/provenance", { params: { path: { object_type: objectType, key }, query: scopeQuery(scope) }, parseAs: "text", signal }))) as Schemas["ProvenanceResponse"],
  })
}

/** Explain one permission snapshot (never executes or edits anything). Input stays exact. */
export function useExplain(scope: Scope) {
  const { connection, client } = useCell()
  return useMutation({
    mutationFn: async (req: { principal: string; action: string; resource: { objectType: string; key: string }; input: unknown; delegationId: string | null }) => {
      const body = stringifyExact({ scope: { orgId: scope.orgId, environmentId: scope.environmentId }, principal: req.principal, action: req.action, resource: req.resource, input: req.input ?? null, delegationId: req.delegationId })
      const text = await run(connection.baseUrl, () => client.POST("/v1/authz/explain", { body: {} as never, bodySerializer: () => body, parseAs: "text" }))
      return parseExact(text) as Schemas["PermissionExplainResponse"]
    },
  })
}

/** A person's desired and observed memberships, with their effective status at read time. */
export function useMemberships(scope: Scope, personId: string) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: ["memberships", connection.baseUrl, scope.orgId, scope.environmentId, personId],
    queryFn: ({ signal }) => run(connection.baseUrl, () => client.GET("/v1/people/{person_id}/memberships", { params: { path: { person_id: personId }, query: scopeQuery(scope) }, signal })),
  })
}

/** Delegation approvals granted by one person. Missing and foreign grantors refuse identically. */
export function useDelegations(scope: Scope, grantorId: string) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: ["delegations", connection.baseUrl, scope.orgId, scope.environmentId, grantorId],
    queryFn: ({ signal }) => run(connection.baseUrl, () => client.GET("/v1/delegations", { params: { query: { ...scopeQuery(scope), grantorId } }, signal })),
  })
}

/** Revoke one delegation. The returned record is the cell's answer; the list is re-read either way. */
export function useRevokeDelegation(scope: Scope, grantorId: string) {
  const { connection, client } = useCell()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => run(connection.baseUrl, () => client.POST("/v1/delegations/{id}/revoke", { params: { path: { id } }, body: { scope: { orgId: scope.orgId, environmentId: scope.environmentId } } })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["delegations", connection.baseUrl, scope.orgId, scope.environmentId, grantorId] }),
  })
}
